import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useFrontAuthStore } from '../../auth/store/authStore';
import Layout from '../../components/layout/Layout';
import { ALL_ROUTES } from '../../../core/router/routes';

const Container = styled.div`
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary[50]} 0%, ${props => props.theme.colors.accent[50]} 100%);
  min-height: calc(100vh - 4rem);
`;

const DashboardCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius['3xl']};
  padding: ${props => props.theme.spacing['3xl']};
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.gray[100]};
`;

const Header = styled.div`
  text-align: center;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['4xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.gray[800]};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
  
  &::before {
    content: '🎉';
    font-size: 2.5rem;
  }
`;

const Welcome = styled.p`
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.gray[600]};
  margin-bottom: ${props => props.theme.spacing.xl};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing['2xl']};

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.gray[100]};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const CardTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const CardDescription = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const CardButton = styled.button<{ $variant: 'primary' | 'secondary' | 'success' }>`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeight.medium};
  transition: all 0.15s ease-in-out;
  cursor: pointer;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background-color: ${props.theme.colors.primary[600]};
          color: ${props.theme.colors.white};
          &:hover {
            background-color: ${props.theme.colors.primary[700]};
          }
        `;
      case 'secondary':
        return `
          background-color: ${props.theme.colors.gray[600]};
          color: ${props.theme.colors.white};
          &:hover {
            background-color: ${props.theme.colors.gray[700]};
          }
        `;
      case 'success':
        return `
          background-color: ${props.theme.colors.green[600]};
          color: ${props.theme.colors.white};
          &:hover {
            background-color: ${props.theme.colors.green[700]};
          }
        `;
    }
  }}
`;

const AccountInfo = styled.div`
  margin-top: ${props => props.theme.spacing['2xl']};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.primary[50]};
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const AccountTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.primary[900]};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const AccountDetails = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const AccountItem = styled.p`
  color: ${props => props.theme.colors.primary[800]};
`;

const AccountLabel = styled.span`
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const UserDashboard: React.FC = () => {
  const { user } = useFrontAuthStore();
  const navigate = useNavigate();

  const handleNavigateToBookList = () => {
    navigate(ALL_ROUTES.BOOK_LIST);
  };

  return (
    <Layout>
      <Container>
        <DashboardCard>
          <Header>
            <Title>사용자 대시보드</Title>
            <Welcome>
              안녕하세요, {user?.name}님! 환영합니다.
            </Welcome>
            
            <CardGrid>
              <FeatureCard>
                <CardTitle>📚 전자책</CardTitle>
                <CardDescription>
                  전자책을 읽고 학습하세요.
                </CardDescription>
                <CardButton $variant="primary" onClick={handleNavigateToBookList}>
                  전자책 보기
                </CardButton>
              </FeatureCard>
              
              <FeatureCard>
                <CardTitle>프로필 관리</CardTitle>
                <CardDescription>
                  개인정보를 수정하고 관리하세요.
                </CardDescription>
                <CardButton $variant="secondary">
                  프로필 보기
                </CardButton>
              </FeatureCard>
              
              <FeatureCard>
                <CardTitle>도움말</CardTitle>
                <CardDescription>
                  사용법과 FAQ를 확인하세요.
                </CardDescription>
                <CardButton $variant="success">
                  도움말 보기
                </CardButton>
              </FeatureCard>
            </CardGrid>
            
            <AccountInfo>
              <AccountTitle>계정 정보</AccountTitle>
              <AccountDetails>
                <AccountItem>
                  <AccountLabel>이름:</AccountLabel> {user?.name}
                </AccountItem>
                <AccountItem>
                  <AccountLabel>이메일:</AccountLabel> {user?.email}
                </AccountItem>
                <AccountItem>
                  <AccountLabel>역할:</AccountLabel> {user?.role === 'premium_user' ? '프리미엄 사용자' : '일반 사용자'}
                </AccountItem>
                <AccountItem>
                  <AccountLabel>가입일:</AccountLabel> {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('ko-KR') : 'N/A'}
                </AccountItem>
              </AccountDetails>
            </AccountInfo>
          </Header>
        </DashboardCard>
      </Container>
    </Layout>
  );
};

export default UserDashboard;

